import { useEffect, useMemo, useRef, useState } from "react";
import { cellBboxPct, detectedCellBbox, type DetectedGrid, type FieldName } from "../lib/cellBbox";

const FIELDS: ReadonlyArray<FieldName> = ["von1", "bis1", "von2", "bis2", "symb"] as const;

/** Read once at module load — used for debug visualization of the grid
 * against the PDF preview during alignment-iteration. Activate via
 * `?debug-grid=1` in the URL. */
const DEBUG_GRID =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("debug-grid") === "1";

interface Props {
  imageUrl: string;
  pdfHrefForOpen: string;
  daysInMonth: number;
  activeCell: { day: number; field: FieldName } | null;
  onCellHover: (cell: { day: number; field: FieldName } | null) => void;
  className?: string;
  /** When false, render only the image without interactive overlays. Used on
   *  mobile where (a) finger precision is insufficient for 7px-tall rows, and
   *  (b) sticky PDF positioning overlaps day-cards making taps unreliable. */
  interactive?: boolean;
  /** Optional URL to fetch backend-detected grid coordinates from. When
   *  available, replaces the heuristic for pixel-accurate placement. */
  gridUrl?: string;
  /** U3 (2026-05-02): When set, the PDF zooms 2× and pans so the cell sits
   *  centered in the viewport. Triggered by auto-focus + jump-to-attention
   *  in TimesheetDetailPage. Pass null to reset to 100%. */
  panToCell?: { day: number; field: FieldName } | null;
  /** Feuert, sobald die Vorschau geladen UND die Overlay-Geometrie berechnet
   *  ist (oder bei Lade-Fehler). Der Caller sperrt damit die Seite bis dahin —
   *  VOR dem Load sitzen die Rahmen sonst kurz falsch (imgBox=null → Fallback
   *  aufs ganze Rechteck) und verleiten zu Fehleingaben. Spiegel zu
   *  ExpensePdfHighlights — gleiche Grundfunktion auf beiden Detailseiten. */
  onReady?: () => void;
}

/**
 * Renders the timesheet PDF preview with absolutely-positioned, transparent
 * overlay divs — one per (day, field) cell — that:
 *
 *   1. Highlight the corresponding cell when `activeCell` matches (driven by
 *      the parent's hover state on the data table → bidirectional link).
 *   2. Notify the parent when the user hovers/taps an overlay → so the table
 *      cell can highlight in turn.
 *
 * Single source: same component on desktop + mobile. Pointer events use
 * `onPointerEnter` / `onPointerLeave` so touch and mouse both work.
 */
export function PdfWithHighlights({
  imageUrl,
  pdfHrefForOpen,
  daysInMonth,
  activeCell,
  onCellHover,
  className,
  interactive = true,
  gridUrl,
  panToCell,
  onReady,
}: Props) {
  const [detected, setDetected] = useState<DetectedGrid | null>(null);
  // Image is rendered with object-fit:contain → it gets letterboxed inside
  // the container if container aspect != image aspect. Overlays positioned
  // by % of CONTAINER would drift on browser resize. We track the actual
  // image bounding box via ResizeObserver + naturalWidth/Height and place
  // the overlay-layer to match the image rect exactly. (User-discovered bug:
  // resize moved overlays to Tag-column instead of von1.)
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imgBox, setImgBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  // Vor dem Bild-Load ist naturalWidth/Height = 0 → imgBox bliebe null → die
  // Overlays fielen aufs ganze Container-Rechteck zurück und säßen FALSCH.
  // Rahmen daher erst zeigen, wenn `loaded`; per onReady signalisieren, dass
  // die Seite jetzt sicher bedienbar ist. (Spiegel zu ExpensePdfHighlights.)
  const [loaded, setLoaded] = useState(false);

  // Neue Vorschau (Karte gewechselt) → wieder in den Lade-Zustand.
  useEffect(() => { setLoaded(false); setImgBox(null); }, [imageUrl]);

  useEffect(() => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;
    const compute = () => {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!nw || !nh) return;
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      if (!cw || !ch) return;
      const naturalRatio = nw / nh;
      const containerRatio = cw / ch;
      let rw, rh, ox, oy;
      if (containerRatio > naturalRatio) {
        // Container wider — image height fills, horizontal letterbox
        rh = ch;
        rw = ch * naturalRatio;
        ox = (cw - rw) / 2;
        oy = 0;
      } else {
        // Container taller — image width fills, vertical letterbox
        rw = cw;
        rh = cw / naturalRatio;
        ox = 0;
        oy = (ch - rh) / 2;
      }
      setImgBox({ left: ox, top: oy, width: rw, height: rh });
    };
    const markReady = () => { compute(); setLoaded(true); onReady?.(); };
    compute();
    // Cache-Fall: Bild kann schon fertig sein, bevor der Listener hängt.
    if (img.complete && img.naturalWidth) markReady();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    img.addEventListener("load", markReady);
    // Lade-Fehler: Seite NICHT ewig sperren → Vorhang lösen.
    img.addEventListener("error", markReady);
    return () => {
      ro.disconnect();
      img.removeEventListener("load", markReady);
      img.removeEventListener("error", markReady);
    };
  }, [imageUrl, onReady]);

  useEffect(() => {
    if (!gridUrl) return;
    // AbortController (statt nur cancelled-Flag) damit der eigentliche
    // HTTP-Request beim unmount abgebrochen wird. Ohne abort lief der
    // grid-detect Aufruf auf der OCR-Sidecar 6-12s weiter und blockte
    // einen Browser-Connection-Slot — bei mehreren wechselnden Karten
    // exhausted das den Pool (Anlassfall 2026-05-19).
    const ctrl = new AbortController();
    fetch(gridUrl, { credentials: "same-origin", signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((g) => {
        if (!g) return;
        if (Array.isArray(g.rows) && g.rows.length === 31 && g.cols && Object.keys(g.cols).length >= 4) {
          setDetected({ rows: g.rows, cols: g.cols });
        }
      })
      .catch(() => { /* abort or net-error → heuristic */ });
    return () => ctrl.abort();
  }, [gridUrl]);

  const overlays = useMemo(() => {
    const list: Array<{
      day: number;
      field: FieldName;
      style: React.CSSProperties;
      isActive: boolean;
      source: "detected" | "heuristic";
    }> = [];
    for (let day = 1; day <= daysInMonth; day++) {
      for (const field of FIELDS) {
        const det = detectedCellBbox(day, field, detected);
        const heur = cellBboxPct(day, field);
        const b = det ?? heur;
        if (!b) continue;
        list.push({
          day,
          field,
          style: {
            top: `${b.topPct}%`,
            left: `${b.leftPct}%`,
            width: `${b.widthPct}%`,
            height: `${b.heightPct}%`,
          },
          isActive: activeCell?.day === day && activeCell?.field === field,
          source: det ? "detected" : "heuristic",
        });
      }
    }
    return list;
  }, [daysInMonth, activeCell, detected]);

  // Position the overlay layer to match the actual image rect (not the
  // container) so percentages stay anchored to image content even when the
  // container has different aspect-ratio than the image (browser resize).
  const overlayLayerStyle: React.CSSProperties = imgBox
    ? {
        position: "absolute",
        left: imgBox.left,
        top: imgBox.top,
        width: imgBox.width,
        height: imgBox.height,
      }
    : { position: "absolute", inset: 0 };

  // Phase 3: zoom + pan. Transform applies to a wrapper containing BOTH the
  // image and the overlay-layer so they stay co-aligned. CSS transform
  // doesn't trigger ResizeObserver (no layout change), so imgBox stays
  // accurate at logical container size.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null);

  const clampZoom = (z: number) => Math.max(1, Math.min(4, z));
  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setZoom((z) => clampZoom(z * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
  };
  const onPointerDown = (e: React.PointerEvent) => {
    if (zoom <= 1.01) return;
    if (e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.startPanX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.startPanY + (e.clientY - dragRef.current.startY),
    });
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (dragRef.current) {
      try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* */ }
      dragRef.current = null;
    }
  };
  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  const zoomedTransform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
  const showControls = interactive;

  // U3: when parent passes panToCell, zoom 2× and translate so the cell
  // bbox lands roughly centered. We compute pan such that the cell's image-
  // local center maps to the container center after `scale(2)` is applied.
  // Setting panToCell=null resets to 100%.
  useEffect(() => {
    if (!imgBox) return;
    if (!panToCell) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      return;
    }
    const det = detectedCellBbox(panToCell.day, panToCell.field, detected);
    const heur = cellBboxPct(panToCell.day, panToCell.field);
    const b = det ?? heur;
    if (!b) return;
    const targetZoom = 2;
    const cellCenterX = imgBox.left + (b.leftPct + b.widthPct / 2) / 100 * imgBox.width;
    const cellCenterY = imgBox.top + (b.topPct + b.heightPct / 2) / 100 * imgBox.height;
    // After scale(targetZoom) around origin (0,0), point (cx,cy) lands at
    // (cx*z + panX, cy*z + panY). We want it at (W/2, H/2) where W,H are the
    // container dimensions.
    const container = containerRef.current;
    if (!container) return;
    const W = container.clientWidth;
    const H = container.clientHeight;
    setZoom(targetZoom);
    setPan({ x: W / 2 - cellCenterX * targetZoom, y: H / 2 - cellCenterY * targetZoom });
  }, [panToCell?.day, panToCell?.field, imgBox, detected]);

  return (
    <div
      ref={containerRef}
      className={`pdf-with-highlights${className ? " " + className : ""}${interactive ? "" : " readonly"}${DEBUG_GRID ? " debug-grid" : ""}${zoom > 1.01 ? " zoomed" : ""}`}
      onWheel={onWheel}
    >
      <div
        className="pdf-zoom-wrapper"
        style={{ transform: zoomedTransform, transformOrigin: "0 0" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
      <a
        href={pdfHrefForOpen}
        target="_blank"
        rel="noopener noreferrer"
        title="PDF in neuem Tab öffnen"
        className="pdf-link"
        onClick={(e) => { if (zoom > 1.01) e.preventDefault(); }}
      >
        <img ref={imgRef} src={imageUrl} alt="PDF-Vorschau" className="pdf-image" loading="eager" decoding="async" draggable={false} />
      </a>
      {(interactive || DEBUG_GRID) && loaded && imgBox && (
        <div className="pdf-overlay-layer" style={overlayLayerStyle} aria-hidden="true">
          {overlays.map(({ day, field, style, isActive, source }) => (
            <div
              key={`${day}-${field}`}
              className={`pdf-overlay-cell src-${source}${isActive ? " active" : ""}`}
              data-day={day}
              data-field={field}
              data-source={source}
              style={style}
              onPointerEnter={interactive ? () => onCellHover({ day, field }) : undefined}
              onPointerLeave={interactive ? () => onCellHover(null) : undefined}
            >
              {DEBUG_GRID && (
                <span className="debug-cell-label">{day}.{field}</span>
              )}
            </div>
          ))}
        </div>
      )}
      </div>
      {showControls && (
        <div className="pdf-zoom-controls" aria-label="Zoom-Steuerung">
          <button type="button" onClick={() => setZoom((z) => clampZoom(z / 1.25))} disabled={zoom <= 1.01} title="Zoom out">−</button>
          <span className="pdf-zoom-level" aria-live="polite">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom((z) => clampZoom(z * 1.25))} disabled={zoom >= 3.99} title="Zoom in">+</button>
          <button type="button" onClick={resetZoom} disabled={zoom <= 1.01 && pan.x === 0 && pan.y === 0} title="Reset">⟲</button>
        </div>
      )}
    </div>
  );
}
